import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { MoreHoriz as MoreIcon } from '@mui/icons-material';
import {
  PATIENT_PANEL_THEME,
  patientPanelBadgeSx,
  patientPanelIconButtonSx,
  patientPanelMobileTabSx,
} from './patientPanelTheme';

export interface PatientPanelMobileTabItem {
  key: string;
  label: string;
  icon: React.ComponentType<{ sx?: object }>;
  badge?: number;
}

const TAB_GAP = 2;
/** Largura reservada — igual ao botão ⋯ das ações (patientPanelIconButtonSx). */
const OVERFLOW_BTN_WIDTH = 30;

function splitTabsByWidth(
  items: PatientPanelMobileTabItem[],
  activeKey: string,
  containerWidth: number,
  widths: number[],
): { visible: PatientPanelMobileTabItem[]; overflow: PatientPanelMobileTabItem[] } {
  if (items.length === 0 || containerWidth <= 0) {
    return { visible: [], overflow: [] };
  }

  const widthForCount = (count: number, reserveOverflow: boolean) => {
    let budget =
      containerWidth
      - (reserveOverflow && count < items.length ? OVERFLOW_BTN_WIDTH + TAB_GAP : 0);
    for (let i = 0; i < count; i += 1) {
      const w = widths[i] ?? 72;
      budget -= w + (i > 0 ? TAB_GAP : 0);
      if (budget < 0) return false;
    }
    return true;
  };

  let count = items.length;
  while (count > 0 && !widthForCount(count, count < items.length)) count -= 1;

  if (count >= items.length) {
    return { visible: items, overflow: [] };
  }

  while (count > 0 && !widthForCount(count, true)) count -= 1;

  const activeIdx = items.findIndex((item) => item.key === activeKey);
  const visibleIndices = new Set<number>();
  for (let i = 0; i < count; i += 1) visibleIndices.add(i);

  if (activeIdx >= count && activeIdx >= 0 && count > 0) {
    visibleIndices.delete(count - 1);
    visibleIndices.add(activeIdx);
  }

  const visible = items.filter((_, index) => visibleIndices.has(index));
  const overflow = items.filter((_, index) => !visibleIndices.has(index));
  return { visible, overflow };
}

interface PatientPanelMobileTabsProps {
  items: PatientPanelMobileTabItem[];
  activeTab: string;
  onChange: (key: string) => void;
}

const PatientPanelMobileTabs: React.FC<PatientPanelMobileTabsProps> = ({
  items,
  activeTab,
  onChange,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [split, setSplit] = useState<{ visible: PatientPanelMobileTabItem[]; overflow: PatientPanelMobileTabItem[] }>(
    () => ({ visible: items, overflow: [] }),
  );

  const recompute = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const widths = items.map((_, index) => measureRefs.current[index]?.offsetWidth ?? 72);
    const next = splitTabsByWidth(items, activeTab, el.clientWidth, widths);
    setSplit((prev) => {
      const sameVisible =
        prev.visible.length === next.visible.length
        && prev.visible.every((t, i) => t.key === next.visible[i]?.key);
      const sameOverflow =
        prev.overflow.length === next.overflow.length
        && prev.overflow.every((t, i) => t.key === next.overflow[i]?.key);
      return sameVisible && sameOverflow ? prev : next;
    });
  }, [items, activeTab]);

  useLayoutEffect(() => {
    recompute();
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => recompute());
    ro.observe(el);
    return () => ro.disconnect();
  }, [recompute]);

  const renderTabBody = (item: PatientPanelMobileTabItem) => {
    const Icon = item.icon;
    return (
      <>
        <Icon sx={{ fontSize: 16, flexShrink: 0 }} />
        {item.badge != null && item.badge > 0 ? (
          <Badge
            badgeContent={item.badge}
            color="primary"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.55rem',
                height: 14,
                minWidth: 14,
                top: 2,
                right: -2,
              },
            }}
          >
            <Box component="span">{item.label}</Box>
          </Badge>
        ) : (
          item.label
        )}
      </>
    );
  };

  const renderTab = (item: PatientPanelMobileTabItem) => {
    const selected = activeTab === item.key;
    return (
      <Box
        key={item.key}
        role="tab"
        aria-selected={selected}
        className="patient-panel-tab-item"
        onClick={() => onChange(item.key)}
        sx={patientPanelMobileTabSx(selected)}
      >
        {renderTabBody(item)}
      </Box>
    );
  };

  const overflowHasActive = split.overflow.some((item) => item.key === activeTab);

  return (
    <>
      <Box
        ref={containerRef}
        className="patient-panel-tabs-mobile"
        role="tablist"
        aria-label="Seções do paciente"
        sx={{
          position: 'relative',
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'flex-end',
          gap: 0.25,
          pl: 0.5,
          pr: 0.75,
          width: '100%',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
            display: 'flex',
            gap: 0.25,
            height: 0,
            overflow: 'hidden',
          }}
        >
          {items.map((item, index) => (
            <Box
              key={`measure-${item.key}`}
              ref={(node: HTMLDivElement | null) => {
                measureRefs.current[index] = node;
              }}
              className="patient-panel-tab-item"
              sx={patientPanelMobileTabSx(false)}
            >
              {renderTabBody(item)}
            </Box>
          ))}
        </Box>
        {split.visible.map((item) => renderTab(item))}
        {split.overflow.length > 0 && (
          <Tooltip title="Mais seções">
            <IconButton
              size="small"
              aria-label="Mais seções"
              aria-haspopup="menu"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{
                ...patientPanelIconButtonSx,
                flexShrink: 0,
                alignSelf: 'center',
                mb: 0.15,
                ...(overflowHasActive
                  ? {
                      color: PATIENT_PANEL_THEME.tealDark,
                      bgcolor: PATIENT_PANEL_THEME.tealSoft,
                      borderColor: PATIENT_PANEL_THEME.teal,
                    }
                  : {}),
              }}
            >
              <MoreIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 200, borderRadius: 2, maxHeight: 'min(360px, 70vh)' } } }}
      >
        {split.overflow.map((item) => {
          const Icon = item.icon;
          const selected = activeTab === item.key;
          return (
            <MenuItem
              key={item.key}
              selected={selected}
              onClick={() => {
                setMenuAnchor(null);
                onChange(item.key);
              }}
              sx={{ fontSize: '0.85rem', py: 0.75 }}
            >
              <ListItemIcon sx={{ minWidth: 32, color: PATIENT_PANEL_THEME.tealDark, '& svg': { fontSize: 18 } }}>
                <Icon sx={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  item.badge != null && item.badge > 0 ? (
                    <Badge badgeContent={item.badge} color="primary" sx={patientPanelBadgeSx}>
                      {item.label}
                    </Badge>
                  ) : (
                    item.label
                  )
                }
                primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: selected ? 700 : 500 }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default PatientPanelMobileTabs;
